/**
 * The provider registry, and the one place a connection turns into something
 * that can move bytes.
 *
 * Connections live in this browser and nowhere else.
 */
import { isExpired, refresh as refreshTokens, type OAuthApp, type TokenSet } from '../oauth';
import type { R2Config } from '../r2';
import { dropboxMeta, dropboxApp, dropboxBackend } from './dropbox';
import { googleDriveMeta, googleDriveApp, googleDriveBackend } from './googledrive';
import { oneDriveMeta, oneDriveApp, oneDriveBackend } from './onedrive';
import { r2Meta, r2Backend } from './r2';
import type { Connection, ProviderId, ProviderMeta, StorageBackend } from './types';

export * from './types';

/** Ordered as recommended: durability first, then free tier. */
export const PROVIDERS: ProviderMeta[] = [dropboxMeta, googleDriveMeta, oneDriveMeta, r2Meta];

export function providerMeta(id: ProviderId): ProviderMeta {
  const m = PROVIDERS.find((p) => p.id === id);
  if (!m) throw new Error(`Unknown provider: ${id}`);
  return m;
}

export function oauthApp(id: string): OAuthApp | undefined {
  switch (id) {
    case 'dropbox':
      return dropboxApp();
    case 'googledrive':
      return googleDriveApp();
    case 'onedrive':
      return oneDriveApp();
    default:
      return undefined;
  }
}

const KEY = 'agl.connection';

export function loadConnection(): Connection | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Connection) : null;
  } catch {
    return null;
  }
}

export function saveConnection(conn: Connection): void {
  localStorage.setItem(KEY, JSON.stringify(conn));
}

export function clearConnection(): void {
  localStorage.removeItem(KEY);
}

/**
 * Turn a stored connection into a working backend, renewing the token first if
 * it is close to lapsing.
 *
 * The refreshed token is written back before use — dropping it would mean a new
 * refresh on every single request, and for providers that rotate the refresh
 * token, losing the only copy that still works.
 */
export async function backendFor(conn: Connection): Promise<StorageBackend> {
  if (conn.provider === 'r2') {
    if (!conn.keys) throw new Error('This R2 connection is missing its keys.');
    return r2Backend(conn.keys as unknown as R2Config);
  }

  let tokens = conn.tokens;
  if (!tokens) throw new Error('This connection is missing its sign-in.');

  if (isExpired(tokens)) {
    const app = oauthApp(conn.provider);
    if (!app) throw new Error('Unknown provider.');
    tokens = await refreshTokens(app, tokens);
    saveConnection({ ...conn, tokens });
  }

  switch (conn.provider) {
    case 'dropbox':
      return dropboxBackend(tokens.accessToken);
    case 'googledrive':
      return googleDriveBackend(tokens.accessToken);
    case 'onedrive':
      return oneDriveBackend(tokens.accessToken);
  }
}

/** Where an envelope lives, for every provider. */
export function envelopePath(id: string): string {
  return `envelopes/${id}.json`;
}

export async function putEnvelope(
  conn: Connection,
  id: string,
  json: string
): Promise<{ path: string; size: number }> {
  const backend = await backendFor(conn);
  const path = envelopePath(id);
  const { size } = await backend.put(path, new TextEncoder().encode(json));
  return { path, size };
}

export async function fetchEnvelope(conn: Connection, id: string): Promise<string | null> {
  const backend = await backendFor(conn);
  const bytes = await backend.get(envelopePath(id));
  return bytes ? new TextDecoder().decode(bytes) : null;
}

export async function removeEnvelope(conn: Connection, id: string): Promise<void> {
  const backend = await backendFor(conn);
  await backend.remove(envelopePath(id));
}

export type { TokenSet };
